import React, { useCallback, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Heading3 } from '@/components/ui/typography'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridColumnProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import {
  TradingAccountActivityDto,
  TradingAccountActivityDtoKey,
} from 'api/Trading-Accounts/Types/TradingAccountActivityDto'
import { Loading } from 'components/Loading'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { formatLocalDateUTC } from 'utils/date'
import { showIpCheckerWindow } from 'utils/media-window/showIpCheckerWindow'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { TableWithExport } from 'utils/table/withExport'

interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const columns: GridColumn[] = [
  {
    title: 'Date from',
    field: TradingAccountActivityDtoKey.dateFrom,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.dateFrom) {
        return <td />
      }
      return (
        <td className='table-columns__date'>{formatLocalDateUTC(props.dataItem.dateFrom, true)}</td>
      )
    },
    className: 'table-columns__date',
    show: true,
  },
  {
    title: 'Date to',
    field: TradingAccountActivityDtoKey.dateTo,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.dateTo) {
        return <td />
      }
      return (
        <td className='table-columns__date'>{formatLocalDateUTC(props.dataItem.dateTo, true)}</td>
      )
    },
    className: 'table-columns__date',
    show: true,
  },
  {
    title: 'IP Address',
    field: TradingAccountActivityDtoKey.ipAddress,
    cell: (props: GridCellProps): JSX.Element => {
      return (
        <td align='center' style={{ textAlign: 'center' }}>
          <Button variant='link' onClick={() => showIpCheckerWindow(props.dataItem.ipAddress)}>
            {props.dataItem.ipAddress}
          </Button>
        </td>
      )
    },
    className: 'table-columns__content',
    show: true,
  },
  {
    title: 'Device Key',
    field: TradingAccountActivityDtoKey.deviceKey,
    className: 'table-columns__content',
    show: true,
  },
  {
    title: 'Count',
    field: TradingAccountActivityDtoKey.count,
    className: 'table-columns__number',
    width: 80,
    show: true,
  },
]

interface WelcomeTradingAccount {
  id: string
  name: string
}

interface LeadWelcomeAccountsActivityProps {
  data: WelcomeTradingAccount
  activities: TradingAccountActivityDto[]
  paginationState: Page
  onGridPageChange(event: GridPageChangeEvent): void
}

export const LeadWelcomeAccountsActivityUI: React.FC<LeadWelcomeAccountsActivityProps> = (
  props
) => {
  const { data, activities, paginationState, onGridPageChange } = props

  return (
    <div style={{ margin: 16 }}>
      <Heading3>
        Welcome Account Analytics <u>{data.name}</u>
      </Heading3>
      <TableWithExport data={activities} columns={columns}>
        <Grid
          resizable
          data={activities.slice(paginationState.skip, paginationState.take + paginationState.skip)}
          skip={paginationState.skip}
          take={paginationState.take}
          total={activities.length}
          onPageChange={onGridPageChange}
          pageable={pageableSettings}
          style={{ zIndex: 0 }}
        >
          {columns
            .filter((c) => c.show)
            .map((column, idx) => (
              <Column key={idx + 2} {...column} />
            ))}
        </Grid>
      </TableWithExport>
    </div>
  )
}

interface OuterProps {
  data: WelcomeTradingAccount
}

export const LeadWelcomeAccountsActivity: React.FC<OuterProps> = ({ data }) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())

  const callback = useCallback(() => {
    return tradingAccountsApiClient.getTradingAccountActivities(data.id)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: activities = [], isLoading } = useFetchOne(callback)

  const handleGridPageChange = (event: GridPageChangeEvent) => {
    setPaginationState({
      skip: event.page.skip,
      take: event.page.take,
    })
  }

  return (
    <Loading isLoading={isLoading}>
      <LeadWelcomeAccountsActivityUI
        data={data}
        activities={activities}
        paginationState={paginationState}
        onGridPageChange={handleGridPageChange}
      />
    </Loading>
  )
}
